import * as pc from "playcanvas";
var distanceToFirstCurve = 0.05;
/* When the outlet points away from the target (e.g. a top-mounted pump outlet
 * feeding cups below), the curve needs to commit much further in the outlet
 * direction so it rises clear of the body before draping down, instead of
 * diving straight back through it. This is the from-side distance used for a
 * full ~180° turn. */
var maxFromCurveDistance = 0.28;
var stepSize = 0.02;
export var Skip;
(function (Skip) {
    Skip[Skip["START"] = 0] = "START";
    Skip[Skip["END"] = 1] = "END";
    Skip[Skip["NONE"] = 2] = "NONE";
})(Skip || (Skip = {}));
var resolvePlaceholderEntity = function (app, node, attachment) {
    var _a;
    var nodeEntity = app.root.findByName(node.id.toString());
    if (!nodeEntity)
        return undefined;
    /* `placeholderEntityName` names a specific model-stack entity that was
     * created with a fresh id on the last mount. After undo/redo the entity is
     * recreated with a new id, so the stored name can be stale — fall back to the
     * node entity (and resolve the placeholder by its stable `placeholderName`)
     * instead of failing to draw the tube. */
    var placeholderEntity = attachment.placeholderEntityName
        ? (_a = nodeEntity.findByName(attachment.placeholderEntityName)) !== null && _a !== void 0 ? _a : nodeEntity
        : nodeEntity;
    return placeholderEntity.findByName(attachment.placeholderName);
};
var getToAndFromEntities = function (props) {
    var app = props.app, fromNode = props.fromNode, fromAttachment = props.fromAttachment, toNode = props.toNode, toAttachment = props.toAttachment;
    return {
        fromEntity: resolvePlaceholderEntity(app, fromNode, fromAttachment),
        toEntity: resolvePlaceholderEntity(app, toNode, toAttachment),
    };
};
export var connectNodes = function (props) {
    var _a = getToAndFromEntities(props), fromEntity = _a.fromEntity, toEntity = _a.toEntity;
    if (!fromEntity || !toEntity)
        return 0;
    var cylinderAsset = props.cylinderAsset, material = props.material, rootEntity = props.rootEntity, diameter = props.diameter, offsetX = props.offsetX;
    var fromPos = fromEntity.getPosition().clone();
    var toPos = toEntity.getPosition().clone();
    var fromDirection = fromEntity.forward.clone().mulScalar(-1);
    var toDirection = toEntity.forward.mulScalar(-1);
    /* Scale how far the curve commits to the outlet direction by how sharply it
     * has to turn to reach the target. `dirToTarget · fromDirection` is +1 when
     * the outlet already points at the target (no extra commitment needed) and
     * -1 when it points directly away (e.g. top outlet, cups below) — in which
     * case we extend the first control point so the tube rises clear of the
     * body before curving down, instead of diving back through it. */
    var dirToTarget = new pc.Vec3().sub2(toPos, fromPos).normalize();
    var turnAmount = pc.math.clamp((1 - fromDirection.dot(dirToTarget)) / 2, 0, 1);
    var fromCurveDistance = pc.math.lerp(distanceToFirstCurve, maxFromCurveDistance, turnAmount);
    var points = [
        fromPos.clone(),
        fromPos.clone().add(fromDirection.clone().mulScalar(fromCurveDistance)),
        toPos.clone().add(toDirection.mulScalar(distanceToFirstCurve)),
        toPos.clone(),
    ];
    return drawCurve(points, stepSize, 0.1, diameter, rootEntity, cylinderAsset, material, offsetX, props.skip);
};
export var connectNodesWithLockedEdgeLength = function (props) {
    var _a = getToAndFromEntities(props), fromEntity = _a.fromEntity, toEntity = _a.toEntity;
    if (!fromEntity || !toEntity)
        return 0;
    var cylinderAsset = props.cylinderAsset, material = props.material, rootEntity = props.rootEntity, diameter = props.diameter, offsetX = props.offsetX, lengthLockedMaterial = props.lengthLockedMaterial;
    var fromVector = fromEntity.getPosition().clone();
    var toVector = toEntity.getPosition().clone();
    var fromDirection = fromEntity.forward.mulScalar(-1);
    var toDirection = toEntity.forward.mulScalar(-1);
    /**
     *   	toEntity
     * 		 	o
     * 		 	|
     * 		 	|
     * 	 o(a2)	|    o(b3)
     * 	 	 	o (b1)
     * 	  	  ^	|
     *   (r1) | |     We need to generate two opposing middle points (a1)&(b1) in order to create the Z curve.
     * 		  | |
     * 		 	o (a1)
     * 	 o(a3)	|    o(b2)
     * 		 	|
     *   	 	|
     * 		 	o
     * 	 	fromEntity
     */
    var vecBetweenFromAndTo = new pc.Vec3().sub2(toVector, fromVector);
    // These two vectors are positioned slightly before and after the middle position between the two entities.
    //  This generates vector starting at fromEntity poiting at(a1)
    var vecPointingAtA1 = new pc.Vec3().add2(fromVector, vecBetweenFromAndTo.clone().mulScalar(0.46));
    // This generates vector starting at fromEntity poiting at (b1)
    var vecPointingAtB1 = new pc.Vec3().add2(fromVector, vecBetweenFromAndTo.clone().mulScalar(0.54));
    // This vector starts at (a1) and points to (b1). This is named (r1).
    var vecBetweenMiddleVectors = new pc.Vec3().sub2(vecPointingAtB1, vecPointingAtA1);
    // We want to generate (a2), so we need to setup a rotation matrix to be able to rotate (r1) to point at (a2)
    var positiveRotationMatrix = new pc.Mat4().setFromEulerAngles(0, 30, 0);
    var positiveR1RotatedTowardsA2 = positiveRotationMatrix.transformVector(vecBetweenMiddleVectors.clone());
    // This generates the vector pointing at (a2). We scale it by 1.3 so that it reaches behind (b1)
    var vecPointingAtA2 = new pc.Vec3().add2(vecPointingAtA1, positiveR1RotatedTowardsA2.clone().mulScalar(1.3));
    // We want to generate (b2), so we need to setup a rotation matrix to be able to rotate (r1) to point at (b2)
    var negativeRotationMatrix = new pc.Mat4().setFromEulerAngles(0, 30, 0);
    // Since (r1) is pointing from (a1) to (b1) we need to scale it by -1 to flip its direction.
    var negativeR1RotatedTowardsB2 = negativeRotationMatrix.transformVector(vecBetweenMiddleVectors.clone().mulScalar(-1));
    // This generates the vector pointing at (b2). We scale it by 1.3 so that it reaches behind (a1)
    var vecPointingAtB2 = new pc.Vec3().add2(vecPointingAtB1, negativeR1RotatedTowardsB2.clone().mulScalar(1.3));
    /* In order to create a smooth curve from the first segment and the last segment of the tube,
    we need opposing control points at both (a1) and (b1), these control points are (a3) and (b3) */
    var vecFromB1ToB2 = new pc.Vec3().sub2(vecPointingAtB2, vecPointingAtB1);
    var controlPointB3 = new pc.Vec3().add2(vecPointingAtB1, vecFromB1ToB2.clone().mulScalar(-1));
    var vecFromA1ToA2 = new pc.Vec3().sub2(vecPointingAtA2, vecPointingAtA1);
    var controlPointA3 = new pc.Vec3().add2(vecPointingAtA1, vecFromA1ToA2.clone().mulScalar(-1));
    var drawCurveFromPoints = function (points, material, skip) {
        if (skip === void 0) { skip = Skip.NONE; }
        drawCurve(points, stepSize, 0.1, diameter, rootEntity, cylinderAsset, material, offsetX, skip, 0.7);
    };
    // Generate 4 bezier control points for the first segment of the tube from the fromEntity to (a1);
    var pointsInFirstSegment = [
        fromEntity.getPosition().clone(),
        fromEntity
            .getPosition()
            .clone()
            .add(fromDirection.mulScalar(distanceToFirstCurve)),
        controlPointA3,
        vecPointingAtA1.clone(),
    ];
    drawCurveFromPoints(pointsInFirstSegment, material, props.skip === Skip.START ? Skip.START : Skip.NONE);
    // Generate 4 bezier control points for the curved middle segment of the tube from (a1) to (b1);
    var pointsInCurvedSegment = [
        vecPointingAtA1.clone(),
        vecPointingAtA2.clone(),
        vecPointingAtB2.clone(),
        vecPointingAtB1.clone(),
    ];
    drawCurveFromPoints(pointsInCurvedSegment, lengthLockedMaterial);
    // Generate 4 bezier control points for the last segment (b1) to toEntity;
    var pointsInLastSegment = [
        vecPointingAtB1.clone(),
        controlPointB3.clone(),
        toEntity
            .getPosition()
            .clone()
            .add(toDirection.mulScalar(distanceToFirstCurve)),
        toEntity.getPosition().clone(),
    ];
    drawCurveFromPoints(pointsInLastSegment, material, props.skip === Skip.END ? Skip.END : Skip.NONE);
    // This return statement of this method is only used when length isn't locked, so no need to return a calculated length of the tube.
    return 0;
};
/**
 * Draw a quadratic bezier curve through three points using cylinders.
 * StepSize determines how many segments the curve is divided into and thus
 * how may cylinders we render. Return the length of the curve.
 *
 * Resource on Bezier curves: https://pomax.github.io/bezierinfo/
 * */
export var drawCurve = function (points, stepSize, assetScale, radius, rootEntity, cylinderModelAsset, tubeMaterial, offsetX, skip, skipFraction) {
    if (offsetX === void 0) { offsetX = 0; }
    if (skip === void 0) { skip = Skip.NONE; }
    if (skipFraction === void 0) { skipFraction = 0.35; }
    if (points.length !== 3 && points.length !== 4)
        return 0;
    var t = 0;
    var bezierPositions = [];
    while (t <= 1) {
        var position = points.length === 3 ? quadraticBezier(t, points) : cubicBezier(t, points);
        bezierPositions.push(position);
        t += stepSize;
    }
    bezierPositions.push(points[points.length - 1]);
    var totalSegments = bezierPositions.length - 1;
    var segmentsToSkip = Math.floor(totalSegments * skipFraction);
    var startIndex = 1;
    var endIndex = totalSegments;
    if (skip === Skip.START) {
        startIndex += segmentsToSkip;
    }
    else if (skip === Skip.END) {
        endIndex -= segmentsToSkip;
    }
    var curveLength = 0;
    for (var ix = startIndex; ix <= endIndex; ix++) {
        var segmentEntity = new pc.Entity();
        segmentEntity.addComponent("model", { type: "asset" });
        rootEntity.addChild(segmentEntity);
        var segmentLength = drawCylinder(bezierPositions[ix - 1], bezierPositions[ix], assetScale, radius, segmentEntity, cylinderModelAsset, tubeMaterial, offsetX);
        curveLength += segmentLength;
    }
    var endpointEntity = new pc.Entity("Sphere");
    rootEntity.addChild(endpointEntity);
    endpointEntity.setLocalScale(radius, radius, radius);
    endpointEntity.setPosition(bezierPositions[bezierPositions.length - 1]);
    if (endpointEntity.model) {
        endpointEntity.model.meshInstances[0].material = tubeMaterial;
    }
    return curveLength;
};
export var drawCylinder = function (from, to, assetScale, radius, entity, modelAsset, material, offsetX) {
    if (offsetX === void 0) { offsetX = 0; }
    if (!entity.model)
        return 0;
    entity.model.asset = modelAsset;
    entity.model.meshInstances[0].material = material;
    var cylinderLength = Math.sqrt(Math.pow(to.x - from.x, 2) +
        Math.pow(to.y - from.y, 2) +
        Math.pow(to.z - from.z, 2)) + 0.001;
    entity.setLocalScale(radius / assetScale, cylinderLength / assetScale, radius / assetScale);
    entity.setPosition(from);
    entity.lookAt(to);
    var angles = entity.getLocalEulerAngles().clone();
    angles.x -= 90;
    entity.setLocalEulerAngles(angles);
    var offset = entity.right.clone().mulScalar(offsetX);
    entity.setPosition(entity.getPosition().clone().add(offset));
    return cylinderLength;
};
export var quadraticBezier = function (t, w) {
    var t2 = t * t;
    var mt = 1 - t;
    var mt2 = mt * mt;
    var firstTerm = w[0].clone().mulScalar(mt2);
    var secondTerm = w[1].clone().mulScalar(2 * mt * t);
    var thirdTerm = w[2].clone().mulScalar(t2);
    return firstTerm.add(secondTerm).add(thirdTerm);
};
export var cubicBezier = function (t, w) {
    var t2 = t * t;
    var t3 = t2 * t;
    var mt = 1 - t;
    var mt2 = mt * mt;
    var mt3 = mt2 * mt;
    var firstTerm = w[0].clone().mulScalar(mt3);
    var secondTerm = w[1].clone().mulScalar(3 * mt2 * t);
    var thirdTerm = w[2].clone().mulScalar(3 * mt * t2);
    var fourthTerm = w[3].clone().mulScalar(t3);
    return firstTerm.add(secondTerm).add(thirdTerm).add(fourthTerm);
};
